import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, ChevronDown, ChevronRight, AlertCircle, Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STAGES = ['Received', 'Folded', 'Damage Checked', 'Pressed', 'Packed', 'Shipped'];

const STAGE_COLORS = {
  'Received':       'bg-gray-100 text-gray-700',
  'Folded':         'bg-sky-100 text-sky-700',
  'Damage Checked': 'bg-amber-100 text-amber-700',
  'Pressed':        'bg-indigo-100 text-indigo-700',
  'Packed':         'bg-emerald-100 text-emerald-700',
  'Shipped':        'bg-green-200 text-green-900',
};

const STAGE_RING = {
  'Received':       'ring-gray-500',
  'Folded':         'ring-sky-500',
  'Damage Checked': 'ring-amber-500',
  'Pressed':        'ring-indigo-500',
  'Packed':         'ring-emerald-500',
  'Shipped':        'ring-green-700',
};

const formatDT = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const InhouseUSNReport = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [stageFilter, setStageFilter] = useState(null); // null | one of STAGES

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) { toast.error('Enter at least 2 characters'); return; }
    setLoading(true);
    setExpanded({});
    setStageFilter(null);
    try {
      const r = await apiClient.get('/inhouse/report/usn-status', { params: { q } });
      setResult(r.data);
      if ((r.data?.total_bundles || 0) === 0) toast.info('No matches found');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); runSearch(); }
  };

  const toggleRow = (bc) => setExpanded(prev => ({ ...prev, [bc]: !prev[bc] }));

  const summary = useMemo(() => {
    if (!result?.bundles?.length) return null;
    const totals = Object.fromEntries(STAGES.map(s => [s, 0]));
    result.bundles.forEach(b => Object.entries(b.stage_counts || {}).forEach(([s, c]) => { totals[s] = (totals[s] || 0) + c; }));
    return totals;
  }, [result]);

  // When a stage filter is active, filter the bundles to only those with USNs in that stage
  // and auto-expand them so the filtered USNs are visible.
  const visibleBundles = useMemo(() => {
    if (!result?.bundles) return [];
    if (!stageFilter) return result.bundles;
    return result.bundles
      .filter(b => (b.stage_counts?.[stageFilter] || 0) > 0)
      .map(b => ({
        ...b,
        usns: (b.usns || []).filter(u => u.latest_stage === stageFilter),
        _filtered_usn_count: (b.stage_counts?.[stageFilter] || 0),
      }));
  }, [result, stageFilter]);

  const handleStageClick = (s) => {
    if (!summary) return;
    if ((summary[s] || 0) === 0 && stageFilter !== s) {
      toast.info(`No USNs in "${s}" stage`);
      return;
    }
    setStageFilter((prev) => {
      const next = prev === s ? null : s;
      if (next) {
        // Auto-expand all bundles so user can see filtered USNs immediately
        const toOpen = {};
        (result?.bundles || []).forEach(b => {
          if ((b.stage_counts?.[next] || 0) > 0) toOpen[b.bundle_code] = true;
        });
        setExpanded(toOpen);
      } else {
        setExpanded({});
      }
      return next;
    });
  };

  const handleReprint = (usn) => {
    if (!usn?.usn_code) return;
    // Shipped / Packed USNs have a final FG label — route to FG label reprint.
    // Earlier stages (Received/Folded/Damage Checked/Pressed) use the FOB USN
    // reprint which reads from the `usns` collection.
    const useFg = ['Shipped', 'Packed'].includes(usn.latest_stage);
    const target = useFg ? '/wms/fg/usn-label-reprint' : '/sections/fob/usn-reprint';
    navigate(target, { state: { presetUsn: usn.usn_code } });
  };

  const clearFilter = () => {
    setStageFilter(null);
    setExpanded({});
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="inhouse-usn-report-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">USN Status Report</h1>
          <p className="text-xs text-gray-500">Search by USN, Bundle No, Design, Shade, Sort No or SKU</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Search bar */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="e.g., BUN020707, 45FAPFN8L, Melon, Aqua, 91790..."
                  autoFocus
                  className="pl-8 text-base"
                  data-testid="usn-search-input"
                />
              </div>
              <Button onClick={runSearch} disabled={loading} data-testid="usn-search-btn">
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary chips */}
        {result && result.total_bundles > 0 && (
          <Card className="bg-white" data-testid="search-summary">
            <CardContent className="pt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-500">Matched:</span>
                <Badge variant="secondary">{result.total_bundles} bundle{result.total_bundles !== 1 ? 's' : ''}</Badge>
                <Badge variant="secondary">{result.total_usns} USNs</Badge>
                {result.match_type && (
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200" variant="outline">
                    by {result.match_type}
                  </Badge>
                )}
                {stageFilter && (
                  <button
                    onClick={clearFilter}
                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 ml-auto"
                    data-testid="clear-stage-filter-btn"
                  >
                    <X className="w-3 h-3" /> Clear &ldquo;{stageFilter}&rdquo; filter
                  </button>
                )}
              </div>
              {summary && (
                <div className="flex flex-wrap gap-1.5">
                  {STAGES.map(s => {
                    const isActive = stageFilter === s;
                    const isDisabled = (summary[s] || 0) === 0 && !isActive;
                    return (
                      <button
                        key={s}
                        onClick={() => handleStageClick(s)}
                        disabled={isDisabled}
                        className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${STAGE_COLORS[s]} border-0 ${
                          isActive ? `ring-2 ${STAGE_RING[s]} shadow-sm` : 'hover:ring-1 hover:ring-gray-300'
                        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid={`stage-filter-${s.toLowerCase().replace(/ /g, '-')}`}
                        title={isActive ? 'Click to clear filter' : `Filter USNs in "${s}" stage`}
                      >
                        {s}: {summary[s] || 0}
                      </button>
                    );
                  })}
                </div>
              )}
              {stageFilter && (
                <p className="text-xs text-gray-600" data-testid="stage-filter-banner">
                  Showing only bundles/USNs in <span className="font-semibold">{stageFilter}</span> stage.
                  {' '}<span className="font-semibold">{visibleBundles.length}</span> bundle{visibleBundles.length !== 1 ? 's' : ''} matched.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && result.bundles && result.bundles.length === 0 && (
          <Card><CardContent className="py-12 text-center text-gray-500 text-sm">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No bundles or USNs match &ldquo;{result.query}&rdquo;
          </CardContent></Card>
        )}

        {visibleBundles.map((b) => {
          const isOpen = !!expanded[b.bundle_code];
          const displayedUsnCount = stageFilter ? (b._filtered_usn_count || 0) : b.usn_count;
          return (
            <Card key={b.bundle_code} className="hover:shadow-sm transition-shadow" data-testid={`usn-bundle-row-${b.bundle_code}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => toggleRow(b.bundle_code)}
                    className="text-gray-400 hover:text-gray-700 mt-0.5 shrink-0"
                    data-testid={`expand-${b.bundle_code}`}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <Package className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-mono font-bold text-sm">{b.bundle_code}</p>
                      <Badge className={`${STAGE_COLORS[b.latest_stage] || 'bg-gray-100'} border-0 text-[10px]`}>
                        {b.latest_stage}
                      </Badge>
                      {b.is_legacy && <Badge variant="outline" className="text-[10px]">Legacy</Badge>}
                      {b.bundle_status && <Badge variant="outline" className="text-[10px] font-mono">{b.bundle_status}</Badge>}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5">
                      {b.design_name && <span><span className="text-gray-500">Design:</span> <span className="font-medium">{b.design_name}</span></span>}
                      {b.shade && <span><span className="text-gray-500">Shade:</span> {b.shade}</span>}
                      {b.size && <span><span className="text-gray-500">Size:</span> {b.size}</span>}
                      {b.sort_no && <span><span className="text-gray-500">Sort:</span> {b.sort_no}</span>}
                      {b.sku_code && <span><span className="text-gray-500">SKU:</span> {b.sku_code}</span>}
                      <span>
                        <span className="text-gray-500">USNs:</span>{' '}
                        <span className="font-semibold">{displayedUsnCount}</span>
                        {stageFilter && displayedUsnCount !== b.usn_count && (
                          <span className="text-gray-400"> / {b.usn_count}</span>
                        )}
                      </span>
                      {b.vendor_name && <span className="text-gray-500 truncate max-w-[240px]">Vendor: {b.vendor_name}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {STAGES.map(s => (b.stage_counts?.[s] ? (
                        <Badge key={s} className={`${STAGE_COLORS[s]} border-0 text-[10px] ${stageFilter === s ? 'ring-1 ring-offset-1 ' + STAGE_RING[s] : ''}`}>{s}: {b.stage_counts[s]}</Badge>
                      ) : null))}
                    </div>
                    {(b.ship_box_number || b.destination_name) && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        Shipped: <span className="font-mono">{b.ship_box_number || '-'}</span>
                        {b.destination_name ? ` → ${b.destination_name}` : ''}
                      </p>
                    )}
                  </div>
                </div>

                {/* Expanded per-USN table */}
                {isOpen && b.usns && b.usns.length > 0 && (
                  <div className="mt-3 border-t pt-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-600 bg-gray-50">
                          <th className="px-2 py-1.5 font-medium">USN</th>
                          <th className="px-2 py-1.5 font-medium">Stage</th>
                          <th className="px-2 py-1.5 font-medium">Folded</th>
                          <th className="px-2 py-1.5 font-medium">Damage Check</th>
                          <th className="px-2 py-1.5 font-medium">Pressed</th>
                          <th className="px-2 py-1.5 font-medium">Packed / Shipped</th>
                          <th className="px-2 py-1.5 font-medium text-right">Reprint</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.usns.map((u) => (
                          <tr key={u.usn_code} className="border-b last:border-0 hover:bg-gray-50" data-testid={`usn-row-${u.usn_code}`}>
                            <td className="px-2 py-1.5 font-mono">{u.usn_code}</td>
                            <td className="px-2 py-1.5">
                              <Badge className={`${STAGE_COLORS[u.latest_stage] || 'bg-gray-100'} border-0 text-[10px]`}>{u.latest_stage}</Badge>
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {u.folded_at ? (
                                <div>
                                  <div>{formatDT(u.folded_at)}</div>
                                  {u.folded_by_name && <div className="text-gray-400 text-[10px]">{u.folded_by_name}</div>}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {u.damage_at ? (
                                <div>
                                  <div className={u.damage_result === 'CLEARED' ? 'text-green-700' : 'text-red-700'}>
                                    {u.damage_result || 'Checked'}
                                  </div>
                                  <div className="text-gray-400 text-[10px]">{formatDT(u.damage_at)}</div>
                                  {u.damage_type && <div className="text-[10px] text-amber-700">{u.damage_type}</div>}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {u.pressed_at ? (
                                <div>
                                  <div>{formatDT(u.pressed_at)}</div>
                                  {u.pressed_by_name && <div className="text-gray-400 text-[10px]">{u.pressed_by_name}</div>}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {u.shipped_at ? (
                                <div>
                                  <div className="text-green-700 font-medium">Shipped</div>
                                  <div className="text-gray-400 text-[10px]">{formatDT(u.shipped_at)}</div>
                                  {u.ship_box_number && <div className="font-mono text-[10px]">{u.ship_box_number}</div>}
                                </div>
                              ) : u.packed_at ? (
                                <div>
                                  <div>Packed</div>
                                  <div className="text-gray-400 text-[10px]">{formatDT(u.packed_at)}</div>
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReprint(u)}
                                className="h-7 text-xs border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                                data-testid={`reprint-usn-btn-${u.usn_code}`}
                                title="Open USN reprint with this USN preloaded"
                              >
                                <Printer className="w-3 h-3 mr-1" />
                                Reprint
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Empty-filter hint */}
        {result && result.bundles.length > 0 && stageFilter && visibleBundles.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-gray-500 text-sm">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No bundles have USNs in &ldquo;{stageFilter}&rdquo; stage.
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={clearFilter} data-testid="clear-stage-filter-empty-btn">
                  Clear filter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InhouseUSNReport;
